import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiUpload } from '../client';
import { queryKeys } from './keys';
import type { FeedbackAdminStatus, FeedbackAttachment, FeedbackDetail, FeedbackInput, FeedbackList, FeedbackListParams } from '../types';

function feedbackQueryString(params: FeedbackListParams): string {
  const query = new URLSearchParams({
    scope: params.scope,
    sort: params.sort,
    order: params.order,
    page: String(params.page),
    page_size: String(params.page_size),
  });
  params.statuses.forEach((s) => query.append('status', s));
  params.categories.forEach((c) => query.append('category', c));
  if (params.q.trim()) query.set('q', params.q.trim());
  return query.toString();
}

export function useFeedbackList(params: FeedbackListParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: queryKeys.feedbackList(params),
    queryFn: () => api<FeedbackList>(`/api/feedback?${feedbackQueryString(params)}`),
    placeholderData: (previous) => previous,
  });
}

/** How many requests wait for an admin's first look: the sidebar's count and the triage banner. */
export function useNewFeedbackCount({ enabled = true }: { enabled?: boolean } = {}) {
  const list = useFeedbackList(
    { scope: 'all', statuses: ['new'], categories: [], q: '', sort: 'last_activity_at', order: 'desc', page: 1, page_size: 1 },
    { enabled },
  );
  return list.data?.total ?? 0;
}

export function useFeedback(id: string) {
  return useQuery({ queryKey: queryKeys.feedback(id), queryFn: () => api<FeedbackDetail>(`/api/feedback/${id}`) });
}

/** Any change to a request refreshes its page, the lists and the dashboard's count. */
function useFeedbackMutation<TVariables>(
  request: (variables: TVariables) => Promise<FeedbackDetail>,
  success: (item: FeedbackDetail, variables: TVariables) => string,
) {
  const client = useQueryClient();
  return useMutation({
    meta: { success },
    mutationFn: request,
    onSuccess: (item) => {
      client.setQueryData(queryKeys.feedback(item.id), item);
      void client.invalidateQueries({ queryKey: ['feedback', 'list'] });
      void client.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useSubmitFeedback() {
  return useFeedbackMutation(
    (body: FeedbackInput) => api<FeedbackDetail>('/api/feedback', 'POST', body),
    () => 'Thanks: your request is filed',
  );
}

export function useCommentFeedback() {
  return useFeedbackMutation(
    ({ id, body, attachment_ids }: { id: string; body: string; attachment_ids: string[] }) =>
      api<FeedbackDetail>(`/api/feedback/${id}/comments`, 'POST', { body, attachment_ids }),
    () => 'Comment added',
  );
}

export function useCancelFeedback() {
  return useFeedbackMutation(
    ({ id, reason }: { id: string; reason?: string }) =>
      api<FeedbackDetail>(`/api/feedback/${id}/cancel`, 'POST', reason ? { reason } : undefined),
    () => 'Request cancelled',
  );
}

export function useSetFeedbackStatus() {
  return useFeedbackMutation(
    ({ id, status, note }: { id: string; status: FeedbackAdminStatus; note?: string }) =>
      api<FeedbackDetail>(`/api/feedback/${id}/status`, 'PATCH', note ? { status, note } : { status }),
    (item) => `Status set to ${item.status.replace('_', ' ')}`,
  );
}

/** Where a feedback file is read; `download` asks for a download rather than the browser's own view. */
export function attachmentUrl(id: string, download = false): string {
  return `/api/feedback/attachments/${id}/content${download ? '?download=true' : ''}`;
}

/** One file for a request or comment being written; quiet, the picker shows each file's outcome. */
export function useUploadAttachment() {
  return useMutation({
    meta: { quiet: true },
    mutationFn: (file: File) => apiUpload<FeedbackAttachment>('/api/feedback/attachments', file),
  });
}

/** Take back a file before it is sent. */
export function useRemoveAttachment() {
  return useMutation({
    meta: { quiet: true },
    mutationFn: (id: string) => api<void>(`/api/feedback/attachments/${id}`, 'DELETE'),
  });
}
